import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchEvents, fetchForsideSektioner, fetchKontaktInformation } from '../lib/cms';
import HeroSlider from '../components/HeroSlider';
import ReviewsSlider from '../components/ReviewsSlider';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [events, setEvents] = useState([]);
  const [sektioner, setSektioner] = useState([]);
  const [kontaktInfo, setKontaktInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [page, eventsData, sektionerData, info] = await Promise.all([
          fetchPageBySlug('index'),
          fetchEvents(),
          fetchForsideSektioner().catch(() => []),
          fetchKontaktInformation().catch(() => null)
        ]);
        setPageData(page);
        setEvents(eventsData || []);
        if (Array.isArray(sektionerData) && sektionerData.length > 0) {
          const sorted = [...sektionerData].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
          setSektioner(sorted);
        }
        setKontaktInfo(info);
      } catch (error) {
        console.error('Fejl ved hentning af data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-dahlia-500 mb-4" />
          <p className="text-forest-900 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{pageData?.meta_titel?.da || pageData?.meta_title?.da || 'Pluk-selv-blomster i Billund, Vejle & Kolding | Dahliahjørnet'}</title>
        <meta name="description" content={pageData?.meta_beskrivelse?.da || pageData?.meta_description?.da || 'Leder du efter pluk-selv-blomster i Billund, Vejle, Kolding eller Midtjylland?'} />
      </Helmet>
      {/* Hero Slider */}
      <HeroSlider />

      {/* Velkomst & Intro */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 flex flex-col gap-6">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-forest-950 leading-tight">
                Pluk-selv-blomster nær Billund – Køb Dahlia knolde og stiklinger mm.
              </h2>
              <p className="text-charcoal-700 leading-relaxed text-lg">
                Her har du mulighed for at kreere din egen buket med pluk-selv-blomster i et utal af farverige blomster i alle størrelser. Køb også dine egne knolde og stiklinger til din have. Er du interesseret i at vide mere, er du velkommen til at begive dig rundt her på hjemmesiden.
              </p>
              <p className="text-forest-800 font-serif italic text-lg border-l-4 border-black pl-4 py-2">
                "Kom og besøg vores hyggelige gård, hvor du også kan være heldig at få lov at klappe katte og høns."
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <a
                  href="https://www.google.dk/maps/dir//Grindstedvej+55,+7184+Vandel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black hover:bg-neutral-900 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <i className="fa-solid fa-map-location-dot" />
                  Find vej til os
                </a>
                <Link
                  to="/om-os"
                  className="border border-black hover:bg-black hover:text-white text-black px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2"
                >
                  Læs mere om os
                  <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/slider-blomst-9.png"
                  alt="Pluk selv Dahlia blomster"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hvem er vi og hvad er Dahliahjørnet */}
      <section className="bg-charcoal-50 py-20 border-t border-charcoal-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Venstre side: Billede og stats */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                {/* Hovedbillede */}
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-lg border border-cream-200">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=750,fit=cover,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/omos.jpg"
                    alt="Hans Jørgen på Dahliahjørnet"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            {/* Højre side: Tekst */}
            <div className="lg:col-span-7 order-1 lg:order-2 flex flex-col gap-6">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-forest-950 leading-tight">
                Hvem er vi og hvad er Dahliahjørnet?
              </h2>
              <div className="prose prose-forest text-charcoal-700 leading-relaxed flex flex-col gap-4">
                <p className="text-lg font-medium text-forest-900">
                  Dahliahjørnet startede i 2019 i rundkørslen ved Vandel mellem Vejle og Billund, hvor Hans Jørgen udlevede drømmen om en pluk-selv mark med dahliaer.
                </p>
                <p>
                  I dag vokser her omkring 6.000 knolde fordelt på ca. 200 forskellige sorter af denne smukke mexicanske skønhed. Udover den farverige pluk-selv mark laver vi også stiklinger af vores egne knolde, som du kan købe med hjem til haven.
                </p>
                <p>
                  Gården er et hyggeligt og levende udflugtsmål for hele familien, hvor nysgerrige høns render frit omkring, og I kan nyde naturen, roen og de fantastiske farver.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mt-4 pt-6 border-t border-cream-200">
                <Link
                  to="/om-os"
                  className="bg-black hover:bg-neutral-900 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  Læs hele historien
                  <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ydelser / Features Grid */}
      <section className="bg-[#9b4d85] py-20 border-y border-[#8b4176] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 flex flex-col gap-4">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white flex items-center justify-center gap-3">
              <i className="fa-solid fa-seedling text-cream-200" aria-hidden="true" />
              Et hjørne fuld af farver
            </h2>
            <p className="text-cream-100">
              Kom forbi Midtjyllands måske hyggeligste gård med pluk-selv-blomster. Vi byder velkommen i et sandt paradis af farver.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sektioner.length > 0 ? (
              sektioner.map((sekt, idx) => {
                const imgUrl = sekt.billede?.[0] || '';
                return (
                  <div key={idx} className="flex flex-col justify-between group">
                    <div className="flex flex-col gap-6">
                      {imgUrl && (
                        <div className="rounded-2xl overflow-hidden">
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                            alt={sekt.titel}
                            className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="text-center">
                        <h3 className="text-xl font-serif font-bold text-white mb-3">
                          {sekt.titel}
                        </h3>
                        <p className="text-cream-100 text-sm leading-relaxed">
                          {sekt.beskrivelse}
                        </p>
                      </div>
                      <div className="mt-6 pt-6 border-t border-white/20 flex items-center justify-between">
                        {sekt.maerke && (
                          <span className="text-xs font-bold uppercase tracking-wider text-cream-200">{sekt.maerke}</span>
                        )}
                        {sekt.knap_link?.startsWith('http') ? (
                          <a
                            href={sekt.knap_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-cream-200 font-semibold text-sm flex items-center gap-1 ml-auto"
                          >
                            {sekt.knap_tekst || 'Find vej'}
                            <i className="fa-solid fa-chevron-right text-xs" />
                          </a>
                        ) : (
                          <Link
                            to={sekt.knap_link || '/'}
                            className="text-white hover:text-cream-200 font-semibold text-sm flex items-center gap-1 ml-auto"
                          >
                            {sekt.knap_tekst || 'Læs mere'}
                            <i className="fa-solid fa-chevron-right text-xs" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <>
                {/* Pluk-selv */}
                <div className="flex flex-col justify-between group">
                  <div className="flex flex-col gap-6">
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/imgi-4-plukselv-24.png"
                        alt="Pluk-selv-blomster"
                        className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-serif font-bold text-white mb-3">
                        Pluk-selv-blomster
                      </h3>
                      <p className="text-cream-100 text-sm leading-relaxed">
                        Over 4.000 planter med dahlia-blomster i alle regnbuens farver. Sæsonen strækker sig fra juli til den første nattefrost.
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/20 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-cream-200">4.000+ planter</span>
                      <a
                        href="https://www.google.dk/maps/dir//Grindstedvej+55,+7184+Vandel"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-cream-200 font-semibold text-sm flex items-center gap-1"
                      >
                        Find vej
                        <i className="fa-solid fa-chevron-right text-xs" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Inspiration */}
                <div className="flex flex-col justify-between group">
                  <div className="flex flex-col gap-6">
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/imgi-5-buket-36.png"
                        alt="Inspiration til din buket"
                        className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-serif font-bold text-white mb-3">
                        Inspiration til din buket
                      </h3>
                      <p className="text-cream-100 text-sm leading-relaxed">
                        Sæt kreativiteten i gang. Vi har masser af ideer til smuk bordpynt, dekorationer og unikke sammensætninger til festlige lejligheder.
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/20 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-cream-200">Kreative ideer</span>
                      <Link
                        to="/ideer-til-bordpynt-og-dekorationer"
                        className="text-white hover:text-cream-200 font-semibold text-sm flex items-center gap-1"
                      >
                        Læs mere
                        <i className="fa-solid fa-chevron-right text-xs" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Knolde & Stiklinger */}
                <div className="flex flex-col justify-between group">
                  <div className="flex flex-col gap-6">
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/imgi-6-stikling-30.png"
                        alt="Knolde og stiklinger"
                        className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-xl font-serif font-bold text-white mb-3">
                        Knolde og stiklinger
                      </h3>
                      <p className="text-cream-100 text-sm leading-relaxed">
                        Dyrk dine egne dahliaer! Vi sælger sunde knolde og stiklinger fra marts og frem, som kan forudbestilles hele året rundt.
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/20 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-cream-200">200+ sorter</span>
                      <Link
                        to="/stiklinger"
                        className="text-white hover:text-cream-200 font-semibold text-sm flex items-center gap-1"
                      >
                        Se udvalg
                        <i className="fa-solid fa-chevron-right text-xs" />
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Arrangementer */}
      <section className="bg-white text-charcoal-900 py-20 border-y border-cream-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7 flex flex-col gap-4">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-forest-950 leading-tight">
                Kommende arrangementer
              </h2>
              <p className="text-charcoal-600 text-lg leading-relaxed max-w-2xl">
                Kom og vær med til vores hyggelige arrangementer på Dahliahjørnet. Vi afholder løbende spændende events, workshops og åben gård, hvor vi deler ud af vores passion for dahliaer.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/arrangementer"
                className="inline-flex bg-charcoal-900 hover:bg-charcoal-950 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-all items-center gap-2"
              >
                Se alle arrangementer
                <i className="fa-solid fa-calendar-days" />
              </Link>
            </div>
          </div>

          {events.length === 0 ? (
            <div className="bg-cream-50 border border-cream-200 p-12 rounded-3xl text-center max-w-3xl mx-auto">
              <i className="fa-solid fa-calendar-xmark text-4xl text-dahlia-500 mb-4 block" />
              <h3 className="text-xl font-serif font-bold text-forest-950 mb-2">Nye arrangementer på vej</h3>
              <p className="text-charcoal-600 text-sm leading-relaxed max-w-md mx-auto">
                Vi planlægger i øjeblikket nye spændende arrangementer for sæsonen. Hold øje med siden eller følg os på Facebook for at få de seneste nyheder!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  className="bg-white border border-cream-100 rounded-3xl p-8 flex flex-col justify-between shadow-md hover:shadow-xl hover:border-dahlia-500/50 transition-all duration-300 group h-full"
                >
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl font-serif font-bold text-forest-950 group-hover:text-dahlia-600 transition-colors">
                      {event.titel?.da || event.title?.da}
                    </h3>
                    <div
                      className="text-charcoal-600 text-sm leading-relaxed line-clamp-3 prose prose-sm"
                      dangerouslySetInnerHTML={{ __html: event.beskrivelse?.da || event.content?.da || '' }}
                    />
                  </div>
                  <div className="mt-8 pt-6 border-t border-cream-200 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-charcoal-400">Event</span>
                    <Link
                      to="/arrangementer"
                      className="text-forest-900 hover:text-dahlia-600 font-semibold text-sm flex items-center gap-1 transition-colors"
                    >
                      Læs mere
                      <i className="fa-solid fa-chevron-right text-xs" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Anmeldelser / Social Proof */}
      <section className="py-20 bg-charcoal-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 flex flex-col gap-4">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-forest-950">Anbefalinger fra glade kunder</h2>
            <p className="text-charcoal-600">
              Vi sætter en stor ære i god service, imødekommenhed og sunde, smukke blomster. Her er et udpluk af vores kunders oplevelser.
            </p>
          </div>

          <ReviewsSlider />
        </div>
      </section>

      {/* Kontakt & Find os */}
      <section className="py-20 bg-white border-t border-cream-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Contact Info & Map */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div className="flex flex-col gap-6">
                <h2 className="text-3xl font-serif font-bold text-forest-950">Vil du høre mere?</h2>
                <p className="text-charcoal-700 text-sm leading-relaxed">
                  Hvis du har spørgsmål til vores priser, åbningstider, salg af pluk-selv-blomster, buketter eller andre varer, så er du altid velkommen til at kontakte os og høre mere. Vi står altid klar til at besvare spørgsmål fra vores kunder.
                </p>

                <ul className="flex flex-col gap-5 text-sm text-charcoal-700 border-t border-cream-100 pt-6">
                  <li className="flex items-start gap-4">
                    <i className="fa-solid fa-location-dot text-xl text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                    <div>
                      <span className="font-bold text-forest-950 block mb-1">Besøg os</span>
                      <a
                        href="https://www.google.dk/maps/dir//Grindstedvej+55,+7184+Vandel"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-dahlia-600 transition-colors"
                      >
                        {kontaktInfo?.adresse || 'Grindstedvej 55'}, {kontaktInfo?.postnr_og_by || '7184 Vandel'}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <i className="fa-solid fa-phone text-lg text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                    <div>
                      <span className="font-bold text-forest-950 block mb-1">Ring til os</span>
                      <a href={`tel:${(kontaktInfo?.telefon || '22 24 39 90').replace(/\s+/g, '')}`} className="hover:text-dahlia-600 transition-colors whitespace-nowrap">
                        {kontaktInfo?.telefon || '22 24 39 90'}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <i className="fa-solid fa-envelope text-lg text-forest-700 mt-1 w-6 text-center flex-shrink-0" />
                    <div>
                      <span className="font-bold text-forest-950 block mb-1">Send en e-mail</span>
                      <a href={`mailto:${kontaktInfo?.email || 'info@dahliahjoernet.dk'}`} className="hover:text-dahlia-600 transition-colors whitespace-nowrap">
                        {kontaktInfo?.email || 'info@dahliahjoernet.dk'}
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Google Maps Embed */}
              <div className="overflow-hidden h-80 rounded-3xl">
                <iframe
                  title="Dahliahjørnet på Google Maps"
                  src="https://maps.google.com/maps?q=Grindstedvej%2055,%207184%20Vandel&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full border-0 grayscale contrast-[1.1]"
                  width="100%"
                  height="100%"
                  allowFullScreen={true}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
